import clsx from 'clsx';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';

interface AlertProps {
  children: React.ReactNode;
  variant?: 'info' | 'success' | 'warning' | 'error';
  title?: string;
  className?: string;
  dismissible?: boolean;
  onClose?: () => void;
  onDismiss?: () => void;
}

export function Alert({ children, variant = 'info', title, className, dismissible, onClose, onDismiss }: AlertProps) {
  const handleClose = onDismiss || onClose;
  const variants = {
    info: {
      container: 'bg-info-light border-info/20',
      icon: 'text-info',
      title: 'text-info-dark',
      text: 'text-info-dark/80',
    },
    success: {
      container: 'bg-success-light border-success/20',
      icon: 'text-success',
      title: 'text-success-dark',
      text: 'text-success-dark/80',
    },
    warning: {
      container: 'bg-warning-light border-warning/20',
      icon: 'text-warning',
      title: 'text-warning-dark',
      text: 'text-warning-dark/80',
    },
    error: {
      container: 'bg-error-light border-error/20',
      icon: 'text-error',
      title: 'text-error-dark',
      text: 'text-error-dark/80',
    },
  };

  const styles = variants[variant];

  return (
    <div className={clsx('rounded-2xl border p-4', styles.container, className)}>
      <div className="flex">
        <div className="flex-shrink-0">
          <AlertIcon variant={variant} className={styles.icon} />
        </div>
        <div className="ml-3 flex-1">
          {title && (
            <h3 className={clsx('text-body-sm font-medium', styles.title)}>{title}</h3>
          )}
          <div className={clsx('text-body-sm', styles.text, title && 'mt-1')}>
            {children}
          </div>
        </div>
        {(handleClose || dismissible) && (
          <button
            type="button"
            onClick={handleClose}
            aria-label="Dismiss"
            className={clsx(
              'ml-3 inline-flex rounded-full p-1 transition-colors hover:bg-surface-secondary',
              styles.icon
            )}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function AlertIcon({ variant, className }: { variant: string; className: string }) {
  const iconClass = clsx('h-5 w-5', className);
  if (variant === 'success') return <CheckCircle2 className={iconClass} aria-hidden />;
  if (variant === 'warning') return <AlertTriangle className={iconClass} aria-hidden />;
  if (variant === 'error') return <AlertCircle className={iconClass} aria-hidden />;
  return <Info className={iconClass} aria-hidden />;
}
